import React from 'react';
import { MessageSquare, Sparkles, Send, MapPin, Users, Plus, Shield } from 'lucide-react';
import { triggerHaptic } from '../../utils/haptics';

interface TeamChatEmptyIllustrationProps {
  targetName?: string;
  isChannel?: boolean;
  onQuickMessage?: (text: string) => void;
  onShareLocation?: () => void;
}

export const TeamChatEmptyIllustration: React.FC<TeamChatEmptyIllustrationProps> = ({
  targetName = "Thread",
  isChannel = false,
  onQuickMessage,
  onShareLocation,
}) => {
  return (
    <div 
      id="team-chat-empty-illustration-container"
      className="relative w-full max-w-lg mx-auto p-6 sm:p-8 text-center bg-gradient-to-b from-indigo-950/90 via-indigo-900/60 to-indigo-950/90 rounded-[2.5rem] border border-white/15 shadow-2xl overflow-hidden space-y-6 group"
    >
      {/* Ambient Backdrop Glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-lime-400/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-lime-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-sky-400/15 rounded-full blur-3xl pointer-events-none animate-pulse" />

      {/* CUSTOM SVG VECTOR ILLUSTRATION FOR TEAM CHAT */}
      <div className="relative w-44 h-44 mx-auto flex items-center justify-center">
        {/* Outer Glowing Energy Rings */}
        <div className="absolute inset-0 rounded-full border-2 border-dashed border-lime-400/30 animate-[spin_30s_linear_infinite]" />
        <div className="absolute inset-2 rounded-full border border-white/10 bg-indigo-950/80 backdrop-blur-md shadow-2xl flex items-center justify-center" />

        {/* Vector Artwork */}
        <svg
          viewBox="0 0 200 200"
          className="w-full h-full relative z-10 drop-shadow-[0_10px_25px_rgba(163,230,53,0.3)]"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Signal Connection Wave Arcs */}
          <path
            d="M 50 50 A 70 70 0 0 1 150 50"
            stroke="#a3e635"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.5"
            strokeLinecap="round"
          />
          <path
            d="M 65 65 A 50 50 0 0 1 135 65"
            stroke="#38bdf8"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.6"
            strokeLinecap="round"
          />

          {/* Main Primary Speech Bubble (Left / Upper) */}
          <g filter="drop-shadow(0 4px 10px rgba(0,0,0,0.5))">
            <rect x="35" y="65" width="85" height="55" rx="16" fill="#1e1b4b" stroke="#a3e635" strokeWidth="2.5" />
            <path d="M 50 120 L 42 135 L 62 120 Z" fill="#1e1b4b" stroke="#a3e635" strokeWidth="2.5" strokeLinejoin="round" />
            {/* Conversation Typing Dots in Main Bubble */}
            <circle cx="58" cy="92" r="4.5" fill="#a3e635" className="animate-pulse" />
            <circle cx="77" cy="92" r="4.5" fill="#a3e635" opacity="0.75" />
            <circle cx="96" cy="92" r="4.5" fill="#a3e635" opacity="0.5" />
          </g>

          {/* Secondary Response Speech Bubble (Right / Lower) */}
          <g filter="drop-shadow(0 4px 12px rgba(0,0,0,0.6))">
            <rect x="85" y="105" width="80" height="50" rx="16" fill="#0f172a" stroke="#38bdf8" strokeWidth="2" />
            <path d="M 145 155 L 155 168 L 135 155 Z" fill="#0f172a" stroke="#38bdf8" strokeWidth="2" strokeLinejoin="round" />
            {/* Basketball / Sport Icon in Reply Bubble */}
            <circle cx="125" cy="130" r="12" fill="#38bdf8" fillOpacity="0.2" stroke="#38bdf8" strokeWidth="1.5" />
            <path d="M 125 118 V 142 M 113 130 H 137" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M 116 122 C 122 125 122 135 116 138" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" fill="none" />
            <path d="M 134 122 C 128 125 128 135 134 138" stroke="#38bdf8" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </g>

          {/* Neon Accent Sparkles */}
          <circle cx="160" cy="55" r="3.5" fill="#facc15" className="animate-ping" />
          <path d="M 160 48 L 162 53 L 167 55 L 162 57 L 160 62 L 158 57 L 153 55 L 158 53 Z" fill="#facc15" />

          <circle cx="35" cy="45" r="2.5" fill="#a3e635" />
          <path d="M 35 40 L 36.5 43.5 L 40 45 L 36.5 46.5 L 35 50 L 33.5 46.5 L 30 45 L 33.5 43.5 Z" fill="#a3e635" />
        </svg>
      </div>

      {/* Title & Explanatory Text */}
      <div className="space-y-2">
        <h4 className="text-lg sm:text-xl font-black italic uppercase text-white tracking-tight">
          {isChannel ? `No Messages in #${targetName}` : `Start Chat with ${targetName}`}
        </h4>
        <p className="text-xs text-indigo-200/80 leading-relaxed max-w-sm mx-auto font-medium">
          {isChannel
            ? 'Be the first athlete to drop a game callout, organize a tournament run, or rally teammates in this channel!'
            : `Break the ice with ${targetName}! Coordinate court pickup runs, challenge them to a match, or share pins.`}
        </p>
      </div>

      {/* Suggested Quick Prompts */}
      <div className="space-y-2 pt-1">
        <span className="text-[10px] font-black uppercase tracking-wider text-indigo-300/70 block">
          Quick Starters
        </span>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              onQuickMessage?.(`Yo ${targetName.split(' ')[0]}, ready to run a pickup match today? 🏀`);
            }}
            className="px-3.5 py-1.5 bg-indigo-900/90 hover:bg-lime-400 hover:text-black text-lime-300 border border-lime-400/30 text-xs font-extrabold rounded-xl transition shadow-md"
          >
            💬 "Ready to run pickup today?"
          </button>

          <button
            type="button"
            onClick={() => {
              triggerHaptic('light');
              onQuickMessage?.("What court are you running at? 📍");
            }}
            className="px-3.5 py-1.5 bg-indigo-900/90 hover:bg-sky-400 hover:text-black text-sky-300 border border-sky-400/30 text-xs font-extrabold rounded-xl transition shadow-md"
          >
            📍 "What court are you at?"
          </button>

          {onShareLocation && (
            <button
              type="button"
              onClick={() => {
                triggerHaptic('light');
                onShareLocation();
              }}
              className="px-3.5 py-1.5 bg-rose-500/20 hover:bg-rose-500 hover:text-white text-rose-300 border border-rose-500/40 text-xs font-black italic uppercase rounded-xl transition shadow-md flex items-center space-x-1"
            >
              <MapPin className="w-3.5 h-3.5" />
              <span>Drop Live Pin</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
